import React, { useState } from "react"
import { useAuth } from "~/lib/auth-context"
import { trpc } from "~/lib/trpc"

import { Header } from "./components/Header"
import { LeftSidebar } from "./components/LeftSidebar"
import { MainChatArea } from "./components/MainChatArea"
import { RightContextPanel } from "./components/RightContextPanel"

type AIChatHubScreenProps = Record<string, never>

const AIChatHubScreen: React.FC<AIChatHubScreenProps> = () => {
  const { user } = useAuth()
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null)
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false)

  // Mutations
  const createProjectMutation = trpc.aiChat.createProject.useMutation()
  const createConversationMutation = trpc.aiChat.createConversation.useMutation()
  const utils = trpc.useUtils()

  // Load projects
  const { data: projects, isLoading: projectsLoading } = trpc.aiChat.listProjects.useQuery({
    include_archived: false,
  })

  // Load conversations (all conversations, optionally filtered by project)
  const { data: conversations, isLoading: conversationsLoading } = trpc.aiChat.listConversations.useQuery({
    filters: {
      project_id: activeProjectId || undefined,
      archived: false,
    },
    limit: 50,
  })

  // Get active conversation details
  const { data: activeConversation, isLoading: conversationLoading } = trpc.aiChat.getConversation.useQuery(
    {
      id: activeConversationId,
      include_messages: true,
      messages_limit: 100,
    },
    {
      enabled: !!activeConversationId,
    },
  )

  // Determine overall loading state - only loading if we're loading conversations or a specific conversation
  const isActuallyLoading = conversationsLoading || (!!activeConversationId && conversationLoading)

  // Set initial active project
  React.useEffect(() => {
    if (projects && projects.length > 0 && !activeProjectId) {
      setActiveProjectId(projects[0].id)
    }
  }, [projects, activeProjectId])

  // Set initial active conversation
  React.useEffect(() => {
    if (conversations && conversations.length > 0 && !activeConversationId) {
      setActiveConversationId(conversations[0].id)
    }
  }, [conversations, activeConversationId])

  // Handle creating a new conversation
  const handleNewConversation = async () => {
    try {
      let projectId = activeProjectId

      // Create default project if none exists
      if (!projectId) {
        const newProject = await createProjectMutation.mutateAsync({
          name: "My AI Chats",
          description: "Default project for AI conversations",
          project_type: "web-chat",
        })
        projectId = newProject.id
        setActiveProjectId(projectId)
        // Refetch projects to update the UI
        void utils.aiChat.listProjects.invalidate()
      }

      const newConversation = await createConversationMutation.mutateAsync({
        project_id: projectId,
        conversation_type: "general",
        primary_persona: "assistant",
      })

      setActiveConversationId(newConversation.id)
      // Refetch conversations to update the UI
      void utils.aiChat.listConversations.invalidate()
    } catch (error) {
      console.error("Failed to create new conversation:", error)
    }
  }

  if (!user) {
    return <div>Please log in to access AI Chat Hub</div>
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border">
        <Header
          onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
          onToggleRightPanel={() => setRightPanelCollapsed(!rightPanelCollapsed)}
          sidebarCollapsed={sidebarCollapsed}
          rightPanelCollapsed={rightPanelCollapsed}
        />
      </div>

      <div className="flex flex-1 pt-16">
        {/* Left Sidebar */}
        <div
          className={`${
            sidebarCollapsed ? "w-16" : "w-80"
          } transition-all duration-300 border-r border-border bg-background`}
        >
          <LeftSidebar
            collapsed={sidebarCollapsed}
            projects={projects || []}
            conversations={conversations || []}
            activeProjectId={activeProjectId}
            activeConversationId={activeConversationId}
            onSelectProject={setActiveProjectId}
            onSelectConversation={setActiveConversationId}
            loading={projectsLoading || conversationsLoading}
          />
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col">
          <MainChatArea
            conversation={activeConversation}
            loading={isActuallyLoading}
            onUpdateConversation={(id, updates) => {
              // Handle conversation updates
              console.log("Update conversation:", id, updates)
            }}
            onNewConversation={handleNewConversation}
          />
        </div>

        {/* Right Context Panel */}
        <div
          className={`${
            rightPanelCollapsed ? "w-0" : "w-80"
          } transition-all duration-300 border-l border-border bg-background overflow-hidden`}
        >
          <RightContextPanel collapsed={rightPanelCollapsed} conversation={activeConversation} />
        </div>
      </div>
    </div>
  )
}

export default AIChatHubScreen
